import {
  AlertCircleIcon,
  Expand01Icon,
  InfoCircleIcon,
  Minimize01Icon,
  Nest01Icon,
  Settings01Icon,
} from '@langchain/untitled-ui-icons';

import { JSONSchema7 } from 'json-schema';
import { LucideSparkles } from 'lucide-react';
import * as React from 'react';
import { useMemo, useState } from 'react';
import ReactFlow, {
  Handle,
  NodeProps,
  Position,
  ReactFlowInstance,
  Node as ReactFlowNode,
  useEdgesState,
  useNodesState,
  useOnViewportChange,
  useReactFlow,
  useStore,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useDebounce } from 'use-debounce';

import { ButtonWithTracking } from '@/components/ButtonWithTracking';
import EmptyState from '@/components/EmptyState';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Tooltip } from '@/design-system';
import { useColorScheme } from '@/hooks/useColorScheme';
import {
  useInitializeGraph,
  useResizeGraph,
  useSetInitialZoom,
} from '@/utils/graph-render-utils';
import { cn } from '@/utils/tailwind';

import {
  useGraphAssistantDebugGraph,
  useGraphAssistantsDebugSchema,
} from '../../../api/useGraphSwr';
import { StateContext } from '../../../control/state';
import {
  type Graph,
  type Updates,
  causedBy,
  getLoadingTasks,
  isTask,
} from '../../../data/misc';
import { useAssistantDefaultConfigSchema } from '../../../hooks/assistants/useAssistantDefaultConfigSchema';
import { StudioFeatureFlagsContext } from '../../../hooks/useStudioFeatureFlagsContext';
import {
  getConfigFieldNodes,
  getSchemaProperties,
} from '../../../json-schema-utils';
import { useAssistantStore } from '../../../store/assistants/assistantsStore';
import {
  getConfigFieldsForNode,
  hashString,
  hslaToStr,
  hslaWithAlpha,
  hslaWithLightness,
  hslaWithMinLightness,
  numberToColor,
} from '../../../utils';
import css from './GraphChartView.module.css';
import { GraphZoomControls } from './GraphZoomControls';
import { NodeMetadataCard, SubgraphMetadataCard } from './NodeMetadataCard';
import { MAX_ZOOM, MIN_ZOOM } from './constants';
import { FloatingEdge as EdgeView } from './flow-edge';
import { overrideReservedLabelsColor } from './graph.utils';
import { useToggleSubgraphExpansion } from './hooks/useToggleSubgraphExpansion';
import { NodeConfiguration } from './node-extras/NodeConfiguration';
import {
  useGraphStore,
  useSaveFitToViewMode,
  useSaveNodePositions,
} from './store';

interface NodeData {
  label: string;
  meta: any;
  isSubgraph: boolean;
}

// ---- Components ----

const NodeView = (props: NodeProps<NodeData>) => {
  const assistantId = useAssistantStore((state) => state.activeAssistantId);
  const config = useAssistantStore((state) => state.config);
  const state = React.useContext(StateContext);

  const nodesOfAnimatedEdges = useStore((store) => {
    return store.edges
      .filter((i) => i.animated)
      .reduce<Set<string>>((memo, item) => {
        memo.add(item.source);
        memo.add(item.target);
        return memo;
      }, new Set());
  });

  const loadingTasks = getGraphLoadingTasks(state.updates);
  const hoverId = state.hoverSelection;

  const { isDarkMode } = useColorScheme();
  const hsla = overrideReservedLabelsColor(
    { label: props.data.label, isDarkMode },
    numberToColor(hashString(props.data.label))
  );

  const colorHsla = overrideReservedLabelsColor(
    { label: props.data.label, isDarkMode },
    hslaWithLightness(hsla, isDarkMode ? 80 : 40)
  );

  const { data: schemas } = useGraphAssistantsDebugSchema(
    assistantId ? [assistantId] : undefined
  );
  const schema = schemas?.[0];
  const { defaultConfigSchema } = useAssistantDefaultConfigSchema(assistantId);
  const { config: configSchema } = schema ?? {};
  const configFieldsForNode = getConfigFieldsForNode({
    configurable: config?.configurable ?? defaultConfigSchema,
    configSchema,
    nodeId: props.id,
  });
  const configFieldsSchema: JSONSchema7 | undefined = useMemo(() => {
    // get the schema for each config field
    if (!configSchema || typeof configSchema !== 'object') return undefined;
    return {
      ...configSchema,
      properties: Object.fromEntries(
        Object.entries(configSchema?.properties ?? {}).filter(([_, value]) =>
          getConfigFieldNodes(value)?.includes(props.id)
        )
      ),
    };
  }, [configSchema, props.id]);

  const nodeConfigurationModalOpen = useAssistantStore(
    (state) => state.nodeConfigurationModalOpen
  );
  const setNodeConfigurationModalOpen = useAssistantStore(
    (state) => state.setNodeConfigurationModalOpen
  );
  const [isNodeConfigButtonHovered, setIsNodeConfigButtonHovered] =
    useState(false);

  const graph = useGraphAssistantDebugGraph(assistantId);
  const toggleSubgraphExpansion = useToggleSubgraphExpansion();

  if (!graph.data) return null;
  // Nodes with interrupts configured in code will be excluded from
  // having the same interrupt configured in the UI.
  const nodes = graph.data?.nodes ?? [];
  const node = nodes.find((node) => node.id === props.id);
  const metadata = (node?.metadata ?? {}) as Record<string, any>;
  const configInterrupts = metadata.__interrupt?.split(',') ?? [];

  const isConfigBeforeBreakpoint = configInterrupts.includes('before');
  const isConfigAfterBreakpoint = configInterrupts.includes('after');

  const isUserBeforeBreakpoint = state.beforeBreakpoints.includes(props.id);
  const isUserAfterBreakpoint = state.afterBreakpoints.includes(props.id);

  const isBeforeBreakpoint = isConfigBeforeBreakpoint || isUserBeforeBreakpoint;
  const isAfterBreakpoint = isConfigAfterBreakpoint || isUserAfterBreakpoint;

  const notStartOrEndNode = props.id !== '__start__' && props.id !== '__end__';

  const mostRecentLogForNode = state.traceLog.findLast(
    (log) => log.entry.name === props.id
  );
  const isMostRecentNodeVisitError =
    mostRecentLogForNode?.entry.type === 'error';

  return (
    <>
      <div className="relative">
        <div key={props.id} className="w-full">
          <Handle
            type="target"
            position={Position.Top}
            id={`${props.id}-target`}
            className="hidden"
          />
          <div className="group flex flex-col place-items-center">
            <div
              className={cn(
                'w-full text-center backdrop-blur-sm transition-all duration-300 ease-in-out',
                loadingTasks.length > 0
                  ? loadingTasks.find((task) => task.name === props.id)
                    ? 'scale-105 opacity-100'
                    : nodesOfAnimatedEdges.has(props.id)
                    ? 'opacity-50'
                    : 'opacity-10'
                  : hoverId != null
                  ? hoverId === props.id ||
                    props.id.startsWith(hoverId + ':') ||
                    (hoverId + ':').startsWith(props.id)
                    ? 'scale-105 opacity-100'
                    : 'opacity-30'
                  : null
              )}
            >
              {Object.keys(getSchemaProperties(configFieldsSchema) ?? {})
                .length > 0 && (
                <Tooltip title="Edit node configuration">
                  <div
                    className="absolute -right-1 -top-1 z-10"
                    onMouseEnter={() => setIsNodeConfigButtonHovered(true)}
                    onMouseLeave={() => setIsNodeConfigButtonHovered(false)}
                  >
                    <ButtonWithTracking
                      eventName="node_configuration_opened"
                      className="flex h-3 w-3 items-center justify-center rounded-full text-primary hover:bg-brand-secondary"
                      style={{
                        backgroundColor: hslaToStr(
                          hslaWithMinLightness(
                            hsla,
                            isNodeConfigButtonHovered ? 45 : 60
                          )
                        ),
                        color: hslaToStr(
                          hslaWithMinLightness(
                            hsla,
                            isNodeConfigButtonHovered ? 20 : 25
                          )
                        ),
                      }}
                      onClick={() =>
                        setNodeConfigurationModalOpen(props.data.label)
                      }
                    >
                      <Settings01Icon className="h-2 w-2" />
                    </ButtonWithTracking>
                  </div>
                </Tooltip>
              )}
              <NodeMetadataCard
                node={props}
                disabled={props.dragging}
                id={props.id}
              >
                <div
                  className={cn(
                    'group relative rounded-md border p-2 font-medium',
                    props.data.isSubgraph && 'cursor-pointer',
                    !notStartOrEndNode && 'rounded-full'
                  )}
                  style={{
                    color: hslaToStr(colorHsla),
                    backgroundColor: hslaToStr(
                      hslaWithAlpha(hsla, props.selected ? 0.2 : 0.1)
                    ),
                    borderColor: hslaToStr(hslaWithMinLightness(hsla, 60)),
                  }}
                  onClick={() => {
                    if (!props.data.isSubgraph) return;
                    toggleSubgraphExpansion(props.id);
                  }}
                >
                  <div className="z-[2] flex items-center justify-center gap-1.5">
                    {props.data.isSubgraph && (
                      <div className="pointer-events-auto relative size-4">
                        <Expand01Icon className="absolute inset-0 size-4 opacity-0 transition-opacity group-hover:opacity-100" />
                        <Nest01Icon className="absolute inset-0 size-4 opacity-100 transition-opacity group-hover:opacity-0" />
                      </div>
                    )}
                    <span className="leading-none">{props.data.label}</span>
                  </div>

                  <span
                    className={cn(
                      css.stripes,
                      'pointer-events-none absolute inset-x-0 z-[1] overflow-hidden opacity-0 transition-opacity',
                      (isBeforeBreakpoint || isAfterBreakpoint) &&
                        'opacity-100',
                      {
                        'top-0 h-1/2 rounded-t-md':
                          isBeforeBreakpoint && !isAfterBreakpoint,
                        'bottom-0 h-1/2 rounded-b-md':
                          !isBeforeBreakpoint && isAfterBreakpoint,
                        'inset-0 rounded-md':
                          isBeforeBreakpoint && isAfterBreakpoint,
                      }
                    )}
                    style={{ color: hslaToStr(hslaWithAlpha(colorHsla, 0.15)) }}
                  />
                </div>
              </NodeMetadataCard>
            </div>
          </div>
          <Handle
            type="source"
            position={Position.Bottom}
            id={`${props.id}-source`}
            className="hidden"
          />
        </div>
        {isMostRecentNodeVisitError && (
          <div className="absolute left-full top-1/2 ml-2 -translate-y-1/2">
            <div className="flex flex-row items-center gap-0.5 rounded-md border border-error bg-error-secondary px-2 py-1 text-error">
              <AlertCircleIcon className="h-2 w-2" />
              <div className="flex flex-row items-center gap-1">
                <span className="text-[8px] font-medium leading-none">
                  Error
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      {nodeConfigurationModalOpen === props.data.label && (
        <NodeConfiguration
          schema={schema}
          onClose={() => setNodeConfigurationModalOpen(undefined)}
          nodeName={props.data.label}
          configFields={configFieldsForNode}
          configFieldsSchema={configFieldsSchema}
        />
      )}
    </>
  );
};

const SubgraphView = (props: NodeProps) => {
  const { isDarkMode } = useColorScheme();
  const hsla = overrideReservedLabelsColor(
    props.data.label,
    numberToColor(hashString(props.data.label))
  );

  const colorHsla = hslaWithLightness(hsla, isDarkMode ? 80 : 40);

  const state = React.useContext(StateContext);
  const loadingTasks = getGraphLoadingTasks(state.updates);

  // TODO: figure out how to obtain config breakpoints for subgraphs
  const isBeforeBreakpoint = state.beforeBreakpoints.includes(props.id);
  const isAfterBreakpoint = state.afterBreakpoints.includes(props.id);

  const toggleSubgraphExpansion = useToggleSubgraphExpansion();

  return (
    <SubgraphMetadataCard
      node={props}
      disabled={props.dragging}
      id={props.id}
      key={props.id}
    >
      <div
        className={cn(
          'group absolute inset-0 -top-6 flex cursor-pointer flex-col place-items-center rounded-lg border border-secondary text-ls-black transition-opacity',
          loadingTasks.length > 0
            ? loadingTasks.find((task) => task.name === props.id)
              ? 'opacity-100'
              : 'opacity-10'
            : state.hoverSelection != null
            ? state.hoverSelection.startsWith(props.id) ||
              props.id.startsWith(state.hoverSelection)
              ? 'opacity-100'
              : 'opacity-30'
            : 'opacity-100'
        )}
        style={{
          color: hslaToStr(colorHsla),
          backgroundColor: hslaToStr(
            hslaWithAlpha(hsla, props.selected ? 0.2 : 0.1)
          ),
          borderColor: hslaToStr(hslaWithMinLightness(hsla, 60)),
        }}
        onClick={() => toggleSubgraphExpansion(props.id)}
      >
        <div className="mt-2 flex items-center justify-center gap-1.5">
          <div className="pointer-events-auto relative size-4">
            <Minimize01Icon className="absolute inset-0 size-4 opacity-0 transition-opacity group-hover:opacity-100" />
            <Nest01Icon className="absolute inset-0 size-4 opacity-100 transition-opacity group-hover:opacity-0" />
          </div>
          <div className="text-xs font-medium">{props.data.label}</div>
        </div>

        <span
          className={cn(
            css.stripes,
            'pointer-events-none absolute inset-x-0 z-[1] overflow-hidden opacity-0 transition-opacity',
            (isBeforeBreakpoint || isAfterBreakpoint) && 'opacity-50',
            {
              'top-0 h-1/2 rounded-t-md':
                isBeforeBreakpoint && !isAfterBreakpoint,
              'bottom-0 h-1/2 rounded-b-md':
                !isBeforeBreakpoint && isAfterBreakpoint,
              'inset-0 rounded-md': isBeforeBreakpoint && isAfterBreakpoint,
            }
          )}
          style={{ color: hslaToStr(hslaWithAlpha(colorHsla, 0.15)) }}
        />
      </div>
    </SubgraphMetadataCard>
  );
};

const nodeTypes = { node: NodeView, subgraph: SubgraphView };
const edgeTypes = { edge: EdgeView };

const getGraphLoadingTasks = (updates: Updates[]) => {
  const loadingTasks = getLoadingTasks(updates);
  return loadingTasks.map((task) => {
    const subgraphs = task.namespace
      .split('|')
      .map((i) => i.split(':').at(0) ?? i);

    const prependNamespace = (name: string) => {
      return [...subgraphs, name].filter(Boolean).join(':');
    };

    const name = prependNamespace(task.payload.name);
    const triggers = task.payload.triggers.map((trigger) => {
      if (trigger.startsWith('start:')) {
        const node = trigger.slice('start:'.length);
        return { type: 'start' as const, node: prependNamespace(node) };
      }

      if (trigger.startsWith('branch:')) {
        const [source, edgeName, target] = trigger.split(':').slice(1);
        return {
          type: 'branch' as const,
          source: prependNamespace(source),
          target: prependNamespace(target),
          edgeName,
        };
      }
      return { type: 'node' as const, node: prependNamespace(trigger) };
    });

    return { name, triggers, step: task.step };
  });
};

export const EmptyGraphSplash = (props: { className?: string | undefined }) => (
  <div
    className={cn(
      props.className,
      'flex h-full flex-col items-center justify-center'
    )}
  >
    <div className="border-1 mb-4 rounded-2xl border border-secondary p-5">
      <LucideSparkles className="text-tertiary" />
    </div>
    <span className="text-lg font-medium text-ls-black">
      Graph appears here
    </span>
    <span className="mt-2 text-sm text-tertiary">Load a graph</span>
  </div>
);

function unique<T>(arr: T[]) {
  return Array.from(new Set(arr));
}

const InnerGraphView = (props: {
  graphData: Graph;
  className?: string | undefined;
}) => {
  const { showGraphZoomControls } = React.useContext(StudioFeatureFlagsContext);
  const state = React.useContext(StateContext);
  const setGraphVisible = useGraphStore((state) => state.setGraphVisible);
  const graphVisible = useGraphStore((state) => state.graphVisible);
  const ref = React.useRef<HTMLDivElement>(null);
  const instanceRef = React.useRef<ReactFlowInstance | null>(null);

  useSaveNodePositions();
  useSaveFitToViewMode();
  useResizeGraph(ref);
  const setInitialZoom = useSetInitialZoom();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const streamSubgraphSelection = React.useMemo(() => {
    const loadingTasks = getLoadingTasks(state.updates);
    return unique(
      loadingTasks
        .map((task) =>
          task.namespace
            .split('|')
            .map((i) => i.split(':').at(0) ?? i)
            .join(':')
        )
        .filter(Boolean)
    );
  }, [state.updates]);

  const [debouncedSubgraphSelection] = useDebounce(
    streamSubgraphSelection,
    100,
    { trailing: true, equalityFn: (a, b) => a.join(',') === b.join(',') }
  );

  const subgraphSelection = React.useMemo(
    () => unique([...debouncedSubgraphSelection, ...state.subgraphSelection]),
    [debouncedSubgraphSelection, state.subgraphSelection]
  );

  React.useEffect(() => {
    const loadingTasks = getGraphLoadingTasks(state.updates);
    const getPreviousTasks = (task) => {
      const tasks = state.updates.filter(isTask);
      const taskStep = task.step;
      return tasks.filter((t) => t.step + 1 === taskStep);
    };
    setEdges((edges) =>
      edges.map((edge) => ({
        ...edge,
        animated: loadingTasks.some((task) => {
          const previousTaskNames = getPreviousTasks(task).map(
            (t) => t.payload.name
          );
          return (
            edge.target === task.name &&
            causedBy({
              source: edge.source,
              target: task.name,
              triggers: task.triggers,
              previousTaskNames,
            })
          );
        }),
      }))
    );
  }, [setEdges, state.updates]);

  const setNodePositions = useGraphStore((state) => state.setNodePositions);
  const nodePositions = useGraphStore((state) => state.nodePositions);

  const setNeedsFitting = useGraphStore((state) => state.setNeedsFitting);
  const fitToViewMode = useGraphStore((state) => state.fitToViewMode);

  const onNodeDragStop = React.useCallback(
    (_: React.MouseEvent, node: ReactFlowNode) => {
      setNodePositions({
        ...nodePositions,
        [node.id]: {
          x: node.position.x,
          y: node.position.y,
          isFixed: true,
        },
      });
      if (fitToViewMode) {
        setNeedsFitting(true);
      }
    },
    [nodePositions, setNodePositions, fitToViewMode, setNeedsFitting]
  );

  const needsInitialZoomRef = React.useRef(true);
  const needsInitialLayoutRef = React.useRef(true);
  const subgraphSelectionSet = React.useMemo(
    () => new Set(subgraphSelection),
    [subgraphSelection]
  );

  useInitializeGraph({
    graphData: props.graphData,
    subgraphSelection: subgraphSelectionSet,
    setNodes,
    setEdges,
    needsInitialLayoutRef,
    needsInitialZoomRef,
  });

  const [layoutKey, setLayoutKey] = useState(0);
  const reRenderKey = !showGraphZoomControls
    ? `${subgraphSelection.join(',')}`
    : `${layoutKey}`;
  const reRenderKeyRef = React.useRef(reRenderKey);

  React.useLayoutEffect(() => {
    if (reRenderKeyRef.current === reRenderKey) return;
    reRenderKeyRef.current = reRenderKey;
    setGraphVisible(false);
  }, [reRenderKey, setGraphVisible]);

  const { getZoom } = useReactFlow();
  const [zoom, setZoom] = useState(getZoom() ?? 1);
  useOnViewportChange({
    onChange: (viewport) => {
      setZoom(viewport.zoom);
    },
  });

  return (
    <div
      ref={ref}
      className={cn(props.className, 'relative flex flex-1 flex-col text-sm')}
    >
      <GraphZoomControls
        zoomLevel={zoom}
        resetLayout={() => {
          setNodePositions({});
          state.clearSubgraphSelection();
          needsInitialLayoutRef.current = true;
          needsInitialZoomRef.current = true;
          setLayoutKey((i) => i + 1);
          if (fitToViewMode) {
            setNeedsFitting(true);
          }
        }}
      />
      <div
        className={cn(
          'absolute inset-0 opacity-0',
          graphVisible && 'opacity-100 transition-opacity'
        )}
      >
        <ReactFlow
          className="flex-1"
          key={reRenderKey}
          nodes={nodes}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onNodeDragStop={onNodeDragStop}
          nodeDragThreshold={5}
          panOnDrag={!fitToViewMode}
          zoomOnPinch={!fitToViewMode}
          zoomOnDoubleClick={!fitToViewMode}
          zoomOnScroll={!fitToViewMode}
          edges={edges}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          edgeTypes={edgeTypes}
          onEdgesChange={onEdgesChange}
          onInit={(inst) => {
            instanceRef.current = inst;
            setInitialZoom(0);
            window.setTimeout(() => {
              setGraphVisible(true);
            }, 100);
          }}
          multiSelectionKeyCode={['Shift']}
          proOptions={{ hideAttribution: true }}
          fitView={fitToViewMode}
        />
      </div>
    </div>
  );
};

export const GraphView = (props: {
  graphData: Graph | undefined;
  isLoading: boolean;
  error: unknown | undefined;
  className?: string | undefined;
}) => {
  const state = React.useContext(StateContext);
  const [key, setKey] = React.useState(0);

  if (!props.graphData) {
    if (props.isLoading && !props.error) {
      return (
        <div className="flex flex-1 items-center justify-center gap-3">
          <div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent text-tertiary" />
          <span className="text-sm text-tertiary">Loading graph...</span>
        </div>
      );
    }

    if (props.error != null && props.error?.toString().includes('HTTP 422')) {
      return (
        <div
          className={cn(
            'flex flex-1 flex-col items-center justify-center gap-3',
            props.className
          )}
        >
          <EmptyState
            icon={<InfoCircleIcon />}
            title="Failed to preview graph"
            description={
              <>
                Previewing dynamic graphs created using the Functional API is
                not supported. These would be graphs created via{' '}
                <code className="inline-block font-mono">entrypoint</code>.
              </>
            }
          />
        </div>
      );
    }

    return (
      <div
        className={cn(
          'flex flex-1 flex-col items-center justify-center gap-3',
          props.className
        )}
      >
        <EmptyState
          title="Failed to preview graph"
          description={
            <>
              Please ensure that the path to the compiled graph is present in{' '}
              <code className="inline-block font-mono">langgraph.json</code>.
            </>
          }
        />

        {props.error != null && (
          <div className="max-w-[728px] px-4">
            <ExpandableErrorAlert error={props.error} />
          </div>
        )}
      </div>
    );
  }

  return (
    <ErrorBoundary
      fallback={(error, onRetry) => (
        <div className="flex flex-1 flex-col items-stretch justify-center gap-4 px-8 text-left">
          <span className="flex items-center justify-between gap-2">
            <span className="text-lg font-medium tracking-tighter">
              Failed to render graph
            </span>
            <button
              type="button"
              onClick={() => {
                setKey((i) => i + 1);

                // clear potentially incorrect state
                state.clearSubgraphSelection();
                state.clearUpdates();

                onRetry?.();
              }}
              className="rounded-md border border-primary px-2 py-1 text-sm"
            >
              Reset
            </button>
          </span>
          <ExpandableErrorAlert error={error} />
        </div>
      )}
    >
      <InnerGraphView
        key={key}
        graphData={props.graphData}
        className={props.className}
      />
    </ErrorBoundary>
  );
};
